import React from 'react';
import { Input, InputNumber, Descriptions, Radio } from 'antd';
import LookupField from '../../../components/LookupField';
import type { F1043Credit } from '../../../services/companyApi';

const { TextArea } = Input;

interface Props {
  isEditing: boolean;
  credit: F1043Credit;
  setCredit: (v: F1043Credit) => void;
  category: string;
  remark: string;
  setRemark: (v: string) => void;
}

const Tab4Credit: React.FC<Props> = React.memo(({ isEditing, credit, setCredit, category, remark, setRemark }) => {
  const update = (field: keyof F1043Credit, value: string | number) => {
    setCredit({ ...credit, [field]: value });
  };

  const showCollection = category === 'C' || category === 'CD' || category === 'F' || category === 'M';
  const showPayment = category === 'D' || category === 'CD' || category === 'M';

  const orderBalance = credit.f1043_31 - credit.f1043_32;
  const purchaseBalance = credit.f1043_36 - credit.f1043_37;
  const creditBalance = credit.f1043_21 - credit.f1043_22;
  const vendorPurchaseBalance = credit.f1043_26 - credit.f1043_27;

  return (
    <div className="space-y-4">
      {/* Currency */}
      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="幣別">
          <Input size="small" value={credit.f1043_11} disabled={!isEditing}
            onChange={e => update('f1043_11', e.target.value)} style={{ width: 120 }} />
        </Descriptions.Item>
        <Descriptions.Item label="">{/* spacer */}</Descriptions.Item>
      </Descriptions>

      {/* Collection & Payment Terms — names are readonly (auto-populated from code) */}
      <Descriptions column={2} size="small" bordered title="收付款條件">
        {showCollection && (
          <>
            <Descriptions.Item label="收款條件代號">
              <LookupField
                lookupType="paymentTerms"
                value={(credit.f1043_13 as string) || ''}
                onChange={(code) => update('f1043_13', code)}
                disabled={!isEditing}
                title="搜尋收款條件"
                style={{ width: 120 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="收款條件名稱">
              <Input size="small" value={credit.f1043_131} disabled
                style={{ width: 200 }} />
            </Descriptions.Item>
          </>
        )}
        {showPayment && (
          <>
            <Descriptions.Item label="付款條件代號">
              <LookupField
                lookupType="paymentTerms"
                value={(credit.f1043_14 as string) || ''}
                onChange={(code) => update('f1043_14', code)}
                disabled={!isEditing}
                title="搜尋付款條件"
                style={{ width: 120 }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="付款條件名稱">
              <Input size="small" value={credit.f1043_141} disabled
                style={{ width: 200 }} />
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      {/* Bank Info */}
      <Descriptions column={3} size="small" bordered title="銀行資訊">
        <Descriptions.Item label="收款銀行">
          <Input size="small" value={credit.f1043_16} disabled={!isEditing}
            onChange={e => update('f1043_16', e.target.value)} style={{ width: 120 }} />
        </Descriptions.Item>
        <Descriptions.Item label="收款銀行類型">
          <Radio.Group value={credit.f1043_161} disabled={!isEditing}
            onChange={e => update('f1043_161', e.target.value)}>
            <Radio value={0}>無</Radio>
            <Radio value={1}>支票</Radio>
            <Radio value={2}>匯款</Radio>
          </Radio.Group>
        </Descriptions.Item>
        <Descriptions.Item label="收款銀行帳號">
          <Input size="small" value={credit.f1043_162} disabled={!isEditing}
            onChange={e => update('f1043_162', e.target.value)} style={{ width: 180 }} />
        </Descriptions.Item>
        <Descriptions.Item label="付款銀行">
          <Input size="small" value={credit.f1043_17} disabled={!isEditing}
            onChange={e => update('f1043_17', e.target.value)} style={{ width: 120 }} />
        </Descriptions.Item>
        <Descriptions.Item label="付款銀行類型">
          <Radio.Group value={credit.f1043_171} disabled={!isEditing}
            onChange={e => update('f1043_171', e.target.value)}>
            <Radio value={0}>無</Radio>
            <Radio value={1}>支票</Radio>
            <Radio value={2}>匯款</Radio>
          </Radio.Group>
        </Descriptions.Item>
        <Descriptions.Item label="付款銀行帳號">
          <Input size="small" value={credit.f1043_172} disabled={!isEditing}
            onChange={e => update('f1043_172', e.target.value)} style={{ width: 180 }} />
        </Descriptions.Item>
      </Descriptions>

      {/* Customer Credit — 受訂/出貨 + 信用額度 merged */}
      <Descriptions column={3} size="small" bordered title="客戶信用額度">
        <Descriptions.Item label="受訂金額">
          <span>{credit.f1043_31.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="出貨金額">
          <span>{credit.f1043_32.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="受訂餘額">
          <span>{orderBalance.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="客戶信用額度">
          <InputNumber size="small" value={credit.f1043_21} disabled={!isEditing}
            onChange={v => update('f1043_21', v ?? 0)} style={{ width: 130 }} />
        </Descriptions.Item>
        <Descriptions.Item label="已用額度">
          <span>{credit.f1043_22.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="信用餘額">
          <span style={{ color: creditBalance < 0 ? 'red' : 'inherit' }}>{creditBalance.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="擔保方式">
          <InputNumber size="small" value={credit.f1043_35} disabled={!isEditing}
            onChange={v => update('f1043_35', v ?? 0)} style={{ width: 130 }} />
        </Descriptions.Item>
        <Descriptions.Item label="">{/* spacer */}</Descriptions.Item>
        <Descriptions.Item label="">{/* spacer */}</Descriptions.Item>
      </Descriptions>

      {/* Vendor Purchase Credit — 採購/進貨 + 採購額度 merged */}
      <Descriptions column={3} size="small" bordered title="廠商採購額度">
        <Descriptions.Item label="採購金額">
          <span>{credit.f1043_36.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="進貨金額">
          <span>{credit.f1043_37.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="採購餘額">
          <span>{purchaseBalance.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="廠商採購額度">
          <InputNumber size="small" value={credit.f1043_26} disabled={!isEditing}
            onChange={v => update('f1043_26', v ?? 0)} style={{ width: 130 }} />
        </Descriptions.Item>
        <Descriptions.Item label="已用額度">
          <span>{credit.f1043_27.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="採購信用餘額">
          <span style={{ color: vendorPurchaseBalance < 0 ? 'red' : 'inherit' }}>{vendorPurchaseBalance.toLocaleString()}</span>
        </Descriptions.Item>
        <Descriptions.Item label="擔保方式">
          <InputNumber size="small" value={credit.f1043_25} disabled={!isEditing}
            onChange={v => update('f1043_25', v ?? 0)} style={{ width: 130 }} />
        </Descriptions.Item>
        <Descriptions.Item label="">{/* spacer */}</Descriptions.Item>
        <Descriptions.Item label="">{/* spacer */}</Descriptions.Item>
      </Descriptions>

      {/* Remark */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">備註</label>
        <TextArea value={remark} onChange={e => setRemark(e.target.value)}
          disabled={!isEditing} rows={4} />
      </div>
    </div>
  );
});

export default Tab4Credit;
